import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, MapPin, Heart, Loader2, ExternalLink, Building2, CheckCircle2, Clipboard, ArrowRight, AlertTriangle, TrendingUp, MessageSquare, Send, ThumbsUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detect Safari browser (including iOS Safari)
const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isSafariBrowser = ua.includes('safari') && !ua.includes('chrome') && !ua.includes('chromium');
  const isIOS = /iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return isSafariBrowser || isIOS;
};

export const ReviewPage = () => {
  const { clinicId } = useParams();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [error, setError] = useState(null);
  const [limitExceeded, setLimitExceeded] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [reviewData, setReviewData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isSafariBrowser] = useState(isSafari());
  
  // Feedback Shield states
  const [flowStep, setFlowStep] = useState('rating'); // 'rating', 'service', 'positive', 'negative', 'feedback_sent'
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    const fetchClinic = async () => {
      try {
        const res = await fetch(`${API}/client/${clinicId}`);
        if (!res.ok) {
          if (res.status === 404) {
            setError('Clinic not found');
          } else {
            throw new Error('Failed to load');
          }
          return;
        }
        const data = await res.json();
        setClinic(data);
      } catch (err) {
        setError('Failed to load clinic information');
      } finally {
        setLoading(false);
      }
    };

    fetchClinic();
  }, [clinicId]);

  // Handle star rating selection
  const handleRatingSelect = (rating) => {
    setSelectedRating(rating);
    if (rating >= 4) {
      // Positive rating - proceed to service selection for AI review
      setFlowStep('service');
    } else {
      // Negative rating - show private feedback form
      setFlowStep('negative');
    }
  };

  // Submit private feedback
  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) {
      toast.error('Please share your feedback');
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      const res = await fetch(`${API}/reviews/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clinicId,
          rating: selectedRating,
          feedback_text: feedbackText,
        }),
      });
      
      if (!res.ok) throw new Error('Failed to submit feedback');
      
      setFlowStep('feedback_sent');
      toast.success('Thank you for your feedback!');
    } catch (err) {
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  // Open Google review directly (for positive ratings)
  // Uses window.open with _blank to force native browser (fixes Instagram/Facebook in-app browser issue)
  const handleOpenGoogleReview = () => {
    if (clinic?.google_review_link) {
      window.open(clinic.google_review_link, '_blank', 'noopener,noreferrer');
    }
  };

  const handleServiceSelect = async (serviceId) => {
    setGenerating(serviceId);
    setReviewData(null);
    setCopied(false);
    setLimitExceeded(false);
    
    try {
      const res = await fetch(`${API}/generate-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clinicId,
          service_id: serviceId,
        }),
      });

      if (!res.ok) {
        const errData = await res.json();
        if (res.status === 429) {
          setLimitExceeded(true);
          setLimitMessage(errData.detail || 'Monthly limit exceeded. Please contact the clinic.');
          setGenerating(null);
          return;
        } else {
          throw new Error(errData.detail || 'Failed to generate review');
        }
      }

      const data = await res.json();
      setReviewData(data);
      setGenerating(null);
      setFlowStep('positive');
      toast.success('Review ready! Tap to continue.');
      
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
      setGenerating(null);
    }
  };

  const handleCopyAndContinue = async () => {
    if (!reviewData) return;
    
    try {
      await navigator.clipboard.writeText(reviewData.review);
      setCopied(true);
      toast.success('Review copied!');
    } catch (err) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = reviewData.review;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(true);
        toast.success('Review copied!');
      } catch (e) {
        toast.error('Please copy manually');
      }
    }
    
    // Open in new tab to force native browser (fixes in-app browser issue)
    window.open(reviewData.review_link, '_blank', 'noopener,noreferrer');
  };

  const handleCopyOnly = async () => {
    if (!reviewData) return;
    
    try {
      await navigator.clipboard.writeText(reviewData.review);
      setCopied(true);
      toast.success('Review copied!');
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = reviewData.review;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      toast.success('Review copied!');
    }
  };

  const openReviewLink = () => {
    if (reviewData?.review_link) {
      // Open in new tab to force native browser (fixes in-app browser issue)
      window.open(reviewData.review_link, '_blank', 'noopener,noreferrer');
    }
  };

  const resetFlow = () => {
    setReviewData(null);
    setCopied(false);
    setLimitExceeded(false);
    setFlowStep('rating');
    setSelectedRating(0);
    setFeedbackText('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card variant="glass" className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground mb-2">
              {error}
            </h2>
            <p className="text-muted-foreground">
              Please check the link or contact the clinic.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Feedback sent confirmation
  if (flowStep === 'feedback_sent') {
    return (
      <div className="min-h-screen bg-background">
        <header className="w-full border-b border-border bg-card/80 backdrop-blur-md">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-success" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold text-foreground">Thank You!</h1>
                <p className="text-sm text-muted-foreground">{clinic?.clinic_name}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
              <ThumbsUp className="w-10 h-10 text-success" />
            </div>
            <Badge variant="success" className="mb-4">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              Feedback Received
            </Badge>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2">
              We appreciate your feedback!
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Your feedback has been shared with {clinic?.clinic_name}. They will use it to improve their services.
            </p>
          </div>

          <Card variant="elevated" className="mb-6">
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">
                Thank you for taking the time to share your experience. Your honest feedback helps us serve you better.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Negative feedback form
  if (flowStep === 'negative') {
    return (
      <div className="min-h-screen bg-background">
        <header className="w-full border-b border-border bg-card/80 backdrop-blur-md">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold text-foreground">Share Your Feedback</h1>
                <p className="text-sm text-muted-foreground">{clinic?.clinic_name}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="flex justify-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-8 h-8 ${star <= selectedRating ? 'text-warning fill-warning' : 'text-muted-foreground'}`}
                />
              ))}
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2">
              We're sorry to hear that
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Your feedback is valuable to us. Please let us know how we can improve.
            </p>
          </div>

          <Card variant="elevated" className="mb-6">
            <CardContent className="pt-6">
              <label className="text-sm font-medium text-foreground mb-2 block">
                Tell us how we can improve
              </label>
              <Textarea
                placeholder="Share your experience and suggestions..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                className="min-h-[120px] mb-4"
                data-testid="feedback-textarea"
              />
              <p className="text-xs text-muted-foreground">
                Your feedback will be sent directly to {clinic?.clinic_name} and will not be posted publicly.
              </p>
            </CardContent>
          </Card>

          <div className="space-y-3">
            <Button 
              variant="premium" 
              size="lg" 
              className="w-full" 
              onClick={handleSubmitFeedback}
              disabled={submittingFeedback}
              data-testid="submit-feedback-btn"
            >
              {submittingFeedback ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Feedback
                </>
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="lg" 
              className="w-full" 
              onClick={resetFlow}
            >
              Go Back
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Limit exceeded screen
  if (limitExceeded) {
    return (
      <div className="min-h-screen bg-background">
        <header className="w-full border-b border-border bg-card/80 backdrop-blur-md">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-warning" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold text-foreground">Limit Reached</h1>
                <p className="text-sm text-muted-foreground">{clinic?.clinic_name}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-warning/10 flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-10 h-10 text-warning" />
            </div>
            <Badge variant="warning" className="mb-4">
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
              Monthly Limit Exceeded
            </Badge>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2">
              Review Limit Reached
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              {limitMessage}
            </p>
          </div>

          <Card variant="elevated" className="mb-6">
            <CardContent className="pt-6 text-center">
              <h3 className="font-heading font-semibold text-foreground mb-4">What can you do?</h3>
              <ul className="space-y-3 text-left max-w-sm mx-auto">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-sm font-bold">1</span>
                  <span className="text-muted-foreground">Contact the clinic to upgrade their plan</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-sm font-bold">2</span>
                  <span className="text-muted-foreground">Wait until next month when limits reset</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-sm font-bold">3</span>
                  <span className="text-muted-foreground">Write your own review directly on Google Maps</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <div className="space-y-3">
            <Button 
              variant="premium" 
              size="lg" 
              className="w-full" 
              onClick={() => window.open(clinic?.google_review_link, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="w-5 h-5" />
              Write Review Manually
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="w-full" 
              onClick={resetFlow}
            >
              Go Back
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (!clinic?.services?.length && flowStep === 'service') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card variant="glass" className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground mb-2">
              No Services Available
            </h2>
            <p className="text-muted-foreground mb-4">
              This clinic hasn&apos;t configured any services yet.
            </p>
            <Button variant="premium" onClick={handleOpenGoogleReview}>
              <ExternalLink className="w-5 h-5" />
              Leave Review on Google
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Review generated - show copy & continue screen (positive flow)
  if (reviewData && flowStep === 'positive') {
    return (
      <div className="min-h-screen bg-background">
        <header className="w-full border-b border-border bg-card/80 backdrop-blur-md">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-success" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold text-foreground">Review Ready!</h1>
                <p className="text-sm text-muted-foreground">{clinic.clinic_name}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
              <Clipboard className="w-10 h-10 text-success" />
            </div>
            
            {copied && !isSafariBrowser ? (
              <>
                <Badge variant="success" className="mb-4">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  Copied to Clipboard
                </Badge>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2">
                  Just Paste & Submit!
                </h2>
                <p className="text-muted-foreground">
                  Your review is copied. Paste it in Google Maps and hit submit.
                </p>
              </>
            ) : (
              <>
                <Badge variant="teal" className="mb-4">
                  <Star className="w-3.5 h-3.5 mr-1.5" />
                  Review Generated
                </Badge>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2">
                  Tap to Continue
                </h2>
                <p className="text-muted-foreground">
                  Tap the button below to copy your review and go to Google Maps.
                </p>
              </>
            )}
          </div>

          <Card variant="elevated" className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-warning fill-warning" />
                  <span className="text-sm font-medium text-muted-foreground">Your Review</span>
                </div>
                <Button variant="outline" size="sm" onClick={handleCopyOnly}>
                  {copied ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-success" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Clipboard className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <p className="text-foreground leading-relaxed bg-muted/50 rounded-lg p-4">
                &ldquo;{reviewData.review}&rdquo;
              </p>
            </CardContent>
          </Card>

          <Card variant="glass" className="mb-6">
            <CardContent className="pt-6">
              <h3 className="font-heading font-semibold text-foreground mb-4">Next Steps:</h3>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-sm font-bold">1</span>
                  <span className="text-muted-foreground">Tap <strong>&quot;Copy & Continue&quot;</strong> below</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-sm font-bold">2</span>
                  <span className="text-muted-foreground">Tap the review text area in Google Maps</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-sm font-bold">3</span>
                  <span className="text-muted-foreground"><strong>Paste</strong> your review (long press → Paste)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-sm font-bold">4</span>
                  <span className="text-muted-foreground">Select your star rating and <strong>Submit</strong></span>
                </li>
              </ol>
            </CardContent>
          </Card>

          <div className="space-y-3">
            <Button 
              variant="premium" 
              size="lg" 
              className="w-full" 
              onClick={handleCopyAndContinue}
              data-testid="copy-continue-btn"
            >
              {copied ? (
                <>
                  <ExternalLink className="w-5 h-5" />
                  Open Google Maps
                </>
              ) : (
                <>
                  <Clipboard className="w-5 h-5" />
                  Copy & Continue
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
            
            {copied && (
              <Button variant="outline" size="lg" className="w-full" onClick={openReviewLink}>
                <ExternalLink className="w-5 h-5" />
                Open Google Maps Again
              </Button>
            )}
            
            <Button 
              variant="ghost" 
              size="lg" 
              className="w-full" 
              onClick={resetFlow}
            >
              Start Over
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Service selection screen (after 4-5 star rating)
  if (flowStep === 'service') {
    return (
      <div className="min-h-screen bg-background">
        <header className="w-full border-b border-border bg-card/80 backdrop-blur-md">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold text-foreground">{clinic.clinic_name}</h1>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  {clinic.city && (
                    <>
                      <MapPin className="w-3 h-3" />
                      {clinic.city}
                    </>
                  )}
                  {clinic.clinic_speciality && (
                    <>
                      <span className="mx-1">•</span>
                      {clinic.clinic_speciality}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="flex justify-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-6 h-6 ${star <= selectedRating ? 'text-warning fill-warning' : 'text-muted-foreground'}`}
                />
              ))}
            </div>
            <Badge variant="success" className="mb-4">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              Thank you for your rating!
            </Badge>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2">
              What service did you receive?
            </h2>
            <p className="text-muted-foreground">
              Select your service and we&apos;ll prepare your review
            </p>
          </div>

          <div className="space-y-3">
            {clinic.services.filter(s => s.is_active !== false).map((service) => (
              <button
                key={service.id}
                onClick={() => handleServiceSelect(service.id)}
                disabled={generating !== null}
                className="w-full text-left"
                data-testid={`service-btn-${service.id}`}
              >
                <Card 
                  variant="glass" 
                  className={`
                    transition-all duration-300 
                    ${generating === service.id 
                      ? 'ring-2 ring-primary shadow-glow' 
                      : 'hover:shadow-lg hover:-translate-y-0.5 hover:border-primary/30'
                    }
                    ${generating !== null && generating !== service.id ? 'opacity-50' : ''}
                  `}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className={`
                        w-14 h-14 rounded-xl flex items-center justify-center shrink-0 transition-colors
                        ${generating === service.id ? 'bg-primary text-primary-foreground' : 'bg-primary/10'}
                      `}>
                        {generating === service.id ? (
                          <Loader2 className="w-6 h-6 animate-spin" />
                        ) : (
                          <Heart className={`w-6 h-6 ${generating === service.id ? '' : 'text-primary'}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-semibold text-foreground text-lg">
                          {service.service_name || service.name}
                        </h3>
                        {(service.outcome_benefit || service.benefit) && (
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {service.outcome_benefit || service.benefit}
                          </p>
                        )}
                        {service.staff_name && (
                          <p className="text-xs text-primary mt-1">
                            with {service.staff_name}{service.staff_role && ` (${service.staff_role})`}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0">
                        {generating === service.id ? (
                          <Badge variant="teal">Generating...</Badge>
                        ) : (
                          <ArrowRight className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>

          <div className="mt-6">
            <Button 
              variant="ghost" 
              size="lg" 
              className="w-full" 
              onClick={resetFlow}
            >
              Go Back
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Initial rating screen (Feedback Shield)
  return (
    <div className="min-h-screen bg-background">
      <header className="w-full border-b border-border bg-card/80 backdrop-blur-md">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold text-foreground">{clinic.clinic_name}</h1>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                {clinic.city && (
                  <>
                    <MapPin className="w-3 h-3" />
                    {clinic.city}
                  </>
                )}
                {clinic.clinic_speciality && (
                  <>
                    <span className="mx-1">•</span>
                    {clinic.clinic_speciality}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <Badge variant="teal" className="mb-4">
            <Star className="w-3.5 h-3.5 mr-1.5" />
            Share Your Experience
          </Badge>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2">
            How was your visit?
          </h2>
          <p className="text-muted-foreground">
            Tap the stars to rate your experience
          </p>
        </div>

        <Card variant="elevated" className="mb-6">
          <CardContent className="pt-8 pb-8">
            <div className="flex justify-center gap-2 sm:gap-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleRatingSelect(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110 focus:outline-none focus:scale-110"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`w-10 h-10 sm:w-14 sm:h-14 transition-colors ${
                      star <= (hoveredRating || selectedRating)
                        ? 'text-warning fill-warning'
                        : 'text-muted-foreground hover:text-warning/50'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-muted-foreground mt-4">
              {hoveredRating === 0 ? 'Select your rating' : 
               hoveredRating <= 3 ? 'We appreciate your honest feedback' : 
               'Glad you had a great experience!'}
            </p>
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            Your feedback helps {clinic.clinic_name} improve their services
          </p>
        </div>
      </main>
    </div>
  );
};
